import { Close, FormatAlignCenter, FormatAlignLeft, FormatAlignRight, Map } from "@mui/icons-material";
import {
  AppBar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { generateRandomHash, useWindowSize } from "../../../core";
import { HidroLayerGroup, PaintTypes } from "../../mapa";
import { MapItemOnText, PropsMapLayerStyle } from "./mapItem";
import { getShapes, ListDamShapes, SelectedItem } from "../../barragem";
import { RequestError } from "../../../services";
import { ErrorModal } from "../../modals";
import { useParams } from "react-router-dom";

export interface StartDataProps {
  title: string;
  position?: "center" | "start" | "end";
  width: number;
  height: number;
  layers: SelectedItem[];
  layersStyles?: PropsMapLayerStyle[];
}

interface Props {
  startData?: StartDataProps;
  onClose?: () => void;
  onInsert?: (opts: StartDataProps) => void;
}

export const InsertMapPage: React.FC<Props> = ({ onClose, onInsert, startData }) => {
  const { height } = useWindowSize();
  const { company, barragem } = useParams();

  const layersMap = useRef<HidroLayerGroup>(new HidroLayerGroup({
    name: "Dados",
    showing: true,
    layers: [],
  }));
  const mapDataWaiting = useRef<StartDataProps>();

  const [mapData, setMapData] = useState<StartDataProps | RequestError>(startData ?? {
    title: "",
    height: 300,
    width: 300,
    layers: [],
  });
  const [getMapData, setGetMapData] = useState(false);

  // Caso não esteja na rota correta
  useEffect(() => {
    if (!barragem) {
      setMapData({
        error: "Erro",
        message: "Barragem ou Empreendimento inválidos",
      });
      return;
    }
  }, []);

  // On finish
  const handleFinish = () => {
    if ("error" in mapData) return;

    if (onInsert) onInsert(mapData);
  };

  // Estilos
  const handleTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMapData({ ...mapData, title: event.target.value });
  };
  const handleWidth = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") return;
    try {
      setMapData({ ...mapData, width: parseFloat(event.target.value) });
    } catch (e) {
      //
    }
  };
  const handleHeight = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") return;
    try {
      setMapData({ ...mapData, height: parseFloat(event.target.value) });
    } catch (e) {
      //
    }
  };
  const handleAlign = (position?: "center" | "start" | "end") => {
    setMapData({ ...mapData, position: position });
  };

  // On atualize layers
  const handleAtualize = async () => {
    if (!mapDataWaiting.current) return;

    const { layers, layersStyles } = mapDataWaiting.current;

    // Abre o loading
    setGetMapData(true);
    layersMap.current.clearLayers(false);

    const layersAdd: SelectedItem[] = layers.filter((layer) => layer.selected);
    const stylesAdd: PaintTypes[] = layersAdd.map((layer) => {
      const stylesNow = (layersStyles ?? []).filter((style) => (style.layer === layer.layer && style.file === layer.name));
      if (stylesNow.length === 0) return {};
      return stylesNow[0].style;
    });

    const layersData = await getShapes(parseInt(barragem!), layersAdd, stylesAdd);

    // Fecha o loading
    setGetMapData(false);

    if ("error" in layersData) {
      setMapData(layersData);
      return;
    }

    layersMap.current.flat(layersData.layers);

    const newGraficData = { ...mapData, layers: layersAdd, layersStyles: layersStyles };
    setMapData(newGraficData);
  };
  // On change
  const handleDataChange = async (layers: SelectedItem[], styles: PropsMapLayerStyle[]) => {
    const layersAdd: SelectedItem[] = layers.filter((layer) => layer.selected);
    const stylesAdd: PropsMapLayerStyle[] = styles.filter(
      (style) => layersAdd.some(
        (layer) => (layer.name === style.file && layer.layer === style.layer)
      )
    );
    const newGraficData = { ...mapData, layers: layersAdd, layersStyles: stylesAdd };
    mapDataWaiting.current = newGraficData as StartDataProps;
  };

  return <>
    {/* App Bar */}
    <AppBar sx={{ position: 'relative' }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <Close />
        </IconButton>
        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
          Mapa
        </Typography>
        <Button autoFocus color="inherit" onClick={handleFinish}>
          {startData ? "Alterar" : "Inserir"}
        </Button>
      </Toolbar>
    </AppBar>

    {/* Conteúdo */}
    {"error" in mapData && <ErrorModal {...mapData} />}
    {!("error" in mapData) &&
      <Box sx={{
        display: "flex",
        flexDirection: "row",
        height: `${height - 76}px`,
      }}>

        {/* Backdrop com CircularProgress */}
        <Backdrop
          open={getMapData}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Opções do mapa */}
        <Box sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "15px",
          width: "35vw"
        }}>
          {/* Título e posição do mapa */}
          <div style={{
            height: "56px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}>
            {/* Título */}
            <TextField
              fullWidth
              label="Título"
              variant="standard"
              defaultValue={mapData.title}
              onChange={handleTitle}
            />
            <div style={{ width: "7.5px" }} />
            <Divider orientation="vertical" />
            <div style={{ width: "7.5px" }} />

            {/* Tamanho */}
            <div style={{
              width: "350px",
              display: "flex",
              borderRadius: "5px",
              alignItems: "center",
            }}>
              <TextField
                label="width"
                value={mapData.width}
                onChange={handleWidth}
                variant="outlined"
                fullWidth
              />
              <div style={{ width: "15px" }} />
              <Typography>X</Typography>
              <div style={{ width: "15px" }} />
              <TextField
                label="height"
                value={mapData.height}
                onChange={handleHeight}
                variant="outlined"
                fullWidth
              />
            </div>
            <div style={{ width: "7.5px" }} />
            <Divider orientation="vertical" />
            <div style={{ width: "7.5px" }} />

            {/* Posição */}
            <div style={{
              height: "36px",
              display: "flex",
              borderRadius: "5px",
              border: "1px solid #EEE"
            }}>
              <IconButton
                onClick={() => handleAlign(mapData.position === "start" ? undefined : "start")}
                color={mapData.position === "start" ? "secondary" : undefined}
              ><FormatAlignLeft /></IconButton>
              <Divider orientation="vertical" />
              <IconButton
                onClick={() => handleAlign(mapData.position === "center" ? undefined : "center")}
                color={mapData.position === "center" ? "secondary" : undefined}
              ><FormatAlignCenter /></IconButton>
              <Divider orientation="vertical" />
              <IconButton
                onClick={() => handleAlign(mapData.position === "end" ? undefined : "end")}
                color={mapData.position === "end" ? "secondary" : undefined}
              ><FormatAlignRight /></IconButton>
            </div>
          </div>

          <div style={{ height: "15px" }} />

          {/* Botão para atualizar o mapa */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
          }}>
            <Typography fontWeight={"bold"} fontSize={16}>Dados:</Typography>
            <div style={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={handleAtualize}
            >
              Atualizar Mapa
            </Button>
          </Box>
          <div style={{ height: "15px" }} />

          {/* Dados */}
          <Box sx={{
            height: "100%",
            display: 'flex',
            flexDirection: 'column',
            overflowY: "auto",
          }}>
            <ListDamShapes
              startLayers={mapData.layers}
              onChange={handleDataChange}
              layersStyles={mapData.layersStyles ?? []}
            />
          </Box>

          {/* Opload de shapes */}

          <div style={{ height: "15px" }} />
          <Divider />
          <div style={{ height: "15px" }} />

          <Box sx={{
            display: 'flex',
            flexDirection: 'row-reverse',
          }}>
            <Button endIcon={<Map />} variant="contained">Adicinar Shapefile</Button>
          </Box>

        </Box>

        {/* Preview */}
        <Box sx={{
          width: "40vw",
          display: "flex",
          flexDirection: "column",
          padding: "15px",
          alignItems: "center",
        }}>
          <MapItemOnText
            key={generateRandomHash(8)}
            {...mapData}
            layers={layersMap.current}
          />
        </Box>
      </Box >
    }
  </>;
};